import { useState, useEffect, useMemo } from 'react'
import TrainingViewer, { TRAINING_MODULES } from './TrainingViewer'
import { downloadTrainingCertificate } from '../utils/certificateGenerator'
import './CAMSCourse.css'

// CAMS exam preparation: the six ACAMS study modules (real slide content + knowledge checks),
// then a final practice exam drawn from every module's question bank. Module completions and
// the certificate are recorded server-side (/api/training/*), so progress follows the user.
const PREP_MODULES = [
  'acams-cams-certification',
  'acams-fundamentals',
  'acams-kyc-due-diligence',
  'acams-sanctions-tfr',
  'acams-transaction-reporting',
  'acams-professional-ethics',
]
const EXAM_PASS_MARK = 75
const COURSE_TITLE = 'CAMS Exam Preparation Course'

function shuffle(list) {
  const a = [...list]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function moduleInfo(id) {
  const m = TRAINING_MODULES[id] || {}
  const slides = m.slides || []
  const quiz = slides.find(s => s.isQuiz)
  return {
    id,
    title: m.title || id,
    description: m.description || '',
    slideCount: slides.filter(s => !s.isQuiz).length,
    questions: quiz?.questions || [],
  }
}

export default function CAMSCourse({ userId, userEmail }) {
  const modules = useMemo(() => PREP_MODULES.map(moduleInfo), [])
  const [assignments, setAssignments] = useState({}) // module_id -> assignment row
  const [certificate, setCertificate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [exam, setExam] = useState(null) // { questions, answers, submitted, score }
  const [savingCert, setSavingCert] = useState(false)

  const load = async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        fetch(`/api/training/member-trainings?userId=${encodeURIComponent(userId)}`, { credentials: 'include' }),
        fetch(`/api/training/certificates?userId=${encodeURIComponent(userId)}`, { credentials: 'include' }),
      ])
      if (!aRes.ok) throw new Error('Could not load your progress')
      const rows = await aRes.json()
      const map = {}
      for (const r of rows) {
        if (!PREP_MODULES.includes(r.module_id)) continue
        // Prefer a completed row if the module is assigned more than once
        if (!map[r.module_id] || r.status === 'completed') map[r.module_id] = r
      }
      setAssignments(map)
      if (cRes.ok) {
        const certs = await cRes.json()
        setCertificate(certs.find(c => c.training_id === 'cams-prep') || null)
      }
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (userId) load() }, [userId])

  const completedCount = PREP_MODULES.filter(id => assignments[id]?.status === 'completed').length
  const allDone = completedCount === PREP_MODULES.length
  const overall = Math.round((completedCount / PREP_MODULES.length) * 100)

  const saveStatus = async (moduleId, status, extra = {}) => {
    const res = await fetch('/api/training/update-training-status', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ moduleId, status, ...extra }),
    })
    if (!res.ok) {
      const e = await res.json().catch(() => ({}))
      throw new Error(e.error || 'Could not save progress')
    }
  }

  const openModule = async (m) => {
    try {
      if (!assignments[m.id] || assignments[m.id].status === 'pending') await saveStatus(m.id, 'in-progress')
      setViewing({ module_id: m.id, module_title: m.title, module_name: m.title })
    } catch (err) {
      alert(err.message)
    }
  }

  const handleModuleComplete = async (moduleId, result) => {
    try {
      await saveStatus(moduleId, 'completed', { quizScore: result?.quizScore })
      if (result?.certificateName) localStorage.setItem(`training_name_${userId}`, result.certificateName)
      await fetch('/api/training/certification-progress', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificationId: 'cams-prep',
          status: 'in-progress',
          progressPercentage: Math.round(((completedCount + (assignments[moduleId]?.status === 'completed' ? 0 : 1)) / PREP_MODULES.length) * 100),
        }),
      }).catch(() => null)
    } catch (err) {
      alert(err.message)
    }
    load()
  }

  const startExam = () => {
    const pool = modules.flatMap(m => m.questions.map(q => ({ ...q, module: m.title })))
    setExam({ questions: shuffle(pool), answers: {}, submitted: false, score: null })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const submitExam = async () => {
    const correct = exam.questions.filter((q, i) => exam.answers[i] === q.correctAnswer).length
    const score = Math.round((correct / exam.questions.length) * 100)
    setExam({ ...exam, submitted: true, score, correct })
    if (score < EXAM_PASS_MARK) return
    setSavingCert(true)
    try {
      const name = localStorage.getItem(`training_name_${userId}`) || ''
      const res = await fetch('/api/training/certificate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, certificateName: name || undefined }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not issue the certificate')
      setCertificate(data)
    } catch (err) {
      alert(err.message)
    } finally {
      setSavingCert(false)
    }
  }

  const downloadCertificate = () => {
    if (!certificate) return
    downloadTrainingCertificate(
      `${COURSE_TITLE}${certificate.score ? ` — final exam ${certificate.score}%` : ''}`,
      certificate.recipient_name || userEmail,
      certificate.issued_at,
      'CrossReg Nexus',
      certificate.code
    )
  }

  if (loading) return <div className="cams-course-container"><p>Loading course…</p></div>

  // ── Final exam ──
  if (exam) {
    const answered = Object.keys(exam.answers).length
    return (
      <div className="cams-course-container">
        <div className="quiz-container">
          <button className="back-btn" onClick={() => setExam(null)}>← Back to modules</button>
          <h2>Final practice exam</h2>
          <p>{exam.questions.length} questions drawn from all six modules · pass mark {EXAM_PASS_MARK}%</p>

          {exam.submitted ? (
            <div className="quiz-results">
              <div className="score-display">
                <div className="score-number">{exam.score}%</div>
                <p className={exam.score >= EXAM_PASS_MARK ? 'passed' : 'failed'}>
                  {exam.score >= EXAM_PASS_MARK ? '✓ Passed' : '✗ Not yet passed'} — {exam.correct} of {exam.questions.length} correct
                </p>
              </div>
              {exam.score >= EXAM_PASS_MARK ? (
                <>
                  <p className="score-message">{savingCert ? 'Issuing your certificate…' : 'Your certificate has been issued.'}</p>
                  {certificate && <button className="download-btn" onClick={downloadCertificate}>📄 Download certificate</button>}
                </>
              ) : (
                <>
                  <p className="score-message">Review the modules covering the questions you missed, then try again.</p>
                  <button className="complete-btn" onClick={startExam}>Retake exam</button>
                </>
              )}
              <div className="quiz-questions" style={{ marginTop: 24 }}>
                {exam.questions.map((q, i) => {
                  const ok = exam.answers[i] === q.correctAnswer
                  return (
                    <div key={i} className="quiz-question" style={{ borderLeft: `4px solid ${ok ? '#10b981' : '#ef4444'}`, paddingLeft: 12 }}>
                      <p><strong>{i + 1}.</strong> {q.question} <small style={{ color: '#6b7280' }}>({q.module})</small></p>
                      {!ok && <p style={{ color: '#065f46' }}>Correct answer: {q.options[q.correctAnswer]}</p>}
                      {q.explanation && <p style={{ color: '#4b5563', fontSize: 13 }}>{q.explanation}</p>}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="quiz-questions">
                {exam.questions.map((q, i) => (
                  <div key={i} className="quiz-question">
                    <p><strong>Question {i + 1}:</strong> {q.question}</p>
                    <div className="options">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className="option">
                          <input
                            type="radio"
                            name={`exam-q${i}`}
                            checked={exam.answers[i] === oi}
                            onChange={() => setExam({ ...exam, answers: { ...exam.answers, [i]: oi } })}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button className="submit-btn" onClick={submitExam} disabled={answered < exam.questions.length}>
                Submit exam ({answered}/{exam.questions.length} answered)
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Module list ──
  return (
    <div className="cams-course-container">
      <div className="cams-header">
        <div className="cams-header-content">
          <h1>{COURSE_TITLE}</h1>
          <p className="subtitle">Six study modules · knowledge check per module · final practice exam</p>
          <div className="progress-info">
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${overall}%` }} /></div>
            <p>{completedCount} of {PREP_MODULES.length} modules completed ({overall}%)</p>
          </div>
        </div>
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="modules-grid">
        {modules.map((m, idx) => {
          const a = assignments[m.id]
          const status = a?.status || 'not-started'
          return (
            <div key={m.id} className={`module-card ${status === 'completed' ? 'completed' : ''}`}>
              <div className="module-header">
                <span className="module-number">{idx + 1}</span>
                <span className={`status ${status === 'completed' ? 'complete' : ''}`}>
                  {status === 'completed' ? `✓ Complete${a.quiz_score != null ? ` · ${a.quiz_score}%` : ''}` : status === 'in-progress' ? 'In progress' : 'Not started'}
                </span>
              </div>
              <h3>{m.title}</h3>
              <p className="objective">{m.description}</p>
              <p className="duration">📑 {m.slideCount} slides · {m.questions.length} quiz questions</p>
              <button className="start-btn" onClick={() => openModule(m)}>
                {status === 'completed' ? '📖 Review' : status === 'in-progress' ? '▶️ Continue' : '▶️ Start'}
              </button>
            </div>
          )
        })}

        {allDone && !certificate && (
          <div className="module-card final-exam-card">
            <div className="module-header">
              <span className="module-number">✓</span>
              <span className="status complete">Ready</span>
            </div>
            <h3>🎖️ Final practice exam</h3>
            <p className="objective">Every question from the six module knowledge checks, in random order. Pass mark {EXAM_PASS_MARK}%.</p>
            <button className="start-btn exam-btn" onClick={startExam}>📝 Take final exam</button>
          </div>
        )}

        {certificate && (
          <div className="module-card certificate-card">
            <div className="module-header">
              <span className="module-number">🎉</span>
              <span className="status complete">Course complete</span>
            </div>
            <h3>{COURSE_TITLE}</h3>
            {certificate.score != null && <p className="exam-score">Final exam: {certificate.score}%</p>}
            <p className="cert-code">Certificate: {certificate.code}</p>
            <div className="certificate-actions">
              <button className="download-btn" onClick={downloadCertificate}>📄 Download certificate</button>
            </div>
          </div>
        )}
      </div>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 24 }}>
        This preparation course is independent of ACAMS. Completing it does not confer the CAMS designation, which is awarded by ACAMS on passing its exam.
      </p>

      {viewing && (
        <TrainingViewer
          training={viewing}
          userId={userId}
          defaultCertificateName={localStorage.getItem(`training_name_${userId}`) || ''}
          onClose={() => setViewing(null)}
          onComplete={(result) => handleModuleComplete(viewing.module_id, result)}
        />
      )}
    </div>
  )
}
